import {
  DatabaseMetadataFieldType,
  DatabaseViewFieldType,
  DocumentFieldsInput,
  DocumentValuesInput,
  FilterOperatorEnum,
  FiltersInput,
  QuerySortType,
  SelectFieldOptionsType,
} from '@/types/schema';

export enum TableViewActionTypes {
  // Sort
  AddSort = 'ADD_SORT',
  SetSort = 'SET_SORT',
  // Filters
  AddFilter = 'ADD_FILTER',
  UpdateFilter = 'UPDATE_FILTER',
  RemoveFilter = 'REMOVE_FILTER',
  UpdateGroupFilterOperator = 'UPDATE_GROUP_FILTER_OPERATOR',
  SetFilters = 'SET_FILTERS',
  // Popovers
  ToggleSortPopover = 'TOGGLE_SORT_POPOVER',
  ToggleFilterPopover = 'TOGGLE_FILTER_POPOVER',
  ToggleColumnPopover = 'TOGGLE_COLUMN_POPOVER',
  // Column Actions
  AddColumn = 'ADD_COLUMN',
  DeleteColumn = 'DELETE_COLUMN',
  DuplicateColumn = 'DUPLICATE_COLUMN',
  UpdateDatabaseViewField = 'UPDATE_DATABASE_VIEW_FIELD',
  UpdateDatabaseMetadataField = 'UPDATE_DATABASE_METADATA_FIELD',
  // Data Actions
  AddNewRow = 'ADD_NEW_ROW',
  AddSelectOption = 'ADD_SELECT_OPTION',
  DuplicateRow = 'DUPLICATE_ROW',
  DeleteRow = 'DELETE_ROW',
  UpdateCellValue = 'UPDATE_CELL_VALUE',
}

// Popovers
interface ToggleFilterPopover {
  type: TableViewActionTypes.ToggleFilterPopover;
  payload: {
    isOpen: boolean;
  };
}

interface ToggleColumnPopoverAction {
  type: TableViewActionTypes.ToggleColumnPopover;
  payload: {
    fieldId: string | null;
  };
}

interface ToggleSortPopover {
  type: TableViewActionTypes.ToggleSortPopover;
  payload: {
    isOpen: boolean;
  };
}

// Filters
interface AddFilterAction {
  type: TableViewActionTypes.AddFilter;
  payload: {
    value?: string | null;
    property?: string | null;
    operator: FilterOperatorEnum;
  };
}

interface RemoveFilterAction {
  type: TableViewActionTypes.RemoveFilter;
  payload: {
    index: number;
  };
}

interface UpdateFilterAction {
  type: TableViewActionTypes.UpdateFilter;
  payload: {
    index: number;
    value?: string | null;
    property?: string | null;
    operator: FilterOperatorEnum;
  };
}

interface UpdateGroupFilterOperatorAction {
  type: TableViewActionTypes.UpdateGroupFilterOperator;
  payload: {
    operator: FilterOperatorEnum;
  };
}

interface SetFiltersAction {
  type: TableViewActionTypes.SetFilters;
  payload: {
    filters: FiltersInput[];
  };
}

// Sort
interface AddSortAction {
  type: TableViewActionTypes.AddSort;
  payload: QuerySortType;
}

interface SetSortAction {
  type: TableViewActionTypes.SetSort;
  payload: QuerySortType[];
}

// Columns
interface AddColumnAction {
  type: TableViewActionTypes.AddColumn;
}

interface DuplicateColumnAction {
  type: TableViewActionTypes.DuplicateColumn;
  payload: {
    fieldId: string;
  };
}

interface DeleteColumnAction {
  type: TableViewActionTypes.DeleteColumn;
  payload: {
    fieldId: string;
  };
}

interface UpdateDatabaseViewFieldAction {
  type: TableViewActionTypes.UpdateDatabaseViewField;
  payload: {
    fieldId: string;
    updates: Partial<Omit<DatabaseViewFieldType, 'fieldId'>>;
  };
}

interface UpdateDatabaseMetadataFieldAction {
  type: TableViewActionTypes.UpdateDatabaseMetadataField;
  payload: {
    fieldId: string;
    updates: Partial<Omit<DatabaseMetadataFieldType, 'fieldId'>>;
  };
}

interface UpdateCellAction {
  type: TableViewActionTypes.UpdateCellValue;
  payload: {
    fieldId: string;
    documentId: string;
    data: DocumentValuesInput;
  };
}

interface AddSelectOptionAction {
  type: TableViewActionTypes.AddSelectOption;
  payload: {
    fieldId: string;
    option: SelectFieldOptionsType;
  };
}

interface AddNewRowAction {
  type: TableViewActionTypes.AddNewRow;
}

interface DuplicateRowAction {
  type: TableViewActionTypes.DuplicateRow;
  payload: {
    fields: DocumentFieldsInput[];
  };
}

interface DeleteRowAction {
  type: TableViewActionTypes.DeleteRow;
  payload: {
    documentId: string;
  };
}

export type TableViewActions =
  | UpdateFilterAction
  | AddFilterAction
  | SetFiltersAction
  | ToggleColumnPopoverAction
  | ToggleFilterPopover
  | ToggleSortPopover
  | AddSortAction
  | SetSortAction
  | RemoveFilterAction
  | DuplicateColumnAction
  | DeleteColumnAction
  | AddColumnAction
  | UpdateGroupFilterOperatorAction
  | UpdateCellAction
  | AddNewRowAction
  | AddSelectOptionAction
  | DuplicateRowAction
  | DeleteRowAction
  | UpdateDatabaseViewFieldAction
  | UpdateDatabaseMetadataFieldAction;

export type onTableViewAction = (action: TableViewActions) => void;
